import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MaskLine, Magnetic, scrollToId } from "@/components/motion";
import { ArrowDownRight } from "lucide-react";

const CATEGORIES = [
  { label: "Career positioning", score: 68 },
  { label: "Achievements and evidence", score: 61 },
  { label: "ATS keywords and relevance", score: 72 },
  { label: "Formatting and structure", score: 83 },
  { label: "Clarity and readability", score: 70 },
];

function ScorecardMock() {
  return (
    <div data-testid="hero-scorecard" className="absolute inset-0 bg-[#141414] border border-white/10 p-5 flex flex-col">
      <div className="flex items-center justify-between pb-3 border-b border-[#27272A]">
        <span className="tracking-label text-[9px] uppercase font-body font-bold text-[#F8F8F5]">
          CV Career Brand Scorecard
        </span>
        <span className="tracking-label text-[8px] uppercase font-body font-bold text-[#52525B]">Sample</span>
      </div>
      <div className="flex items-end justify-between py-4 border-b border-[#27272A]">
        <div className="flex items-baseline gap-1">
          <span className="font-display text-5xl font-bold text-[#DFFF00] leading-none">74</span>
          <span className="font-body text-xs text-[#52525B]">/100</span>
        </div>
        <div className="text-right">
          <p className="tracking-label text-[8px] uppercase font-body font-bold text-[#52525B]">Overall band</p>
          <p className="font-body text-xs text-[#F8F8F5] font-semibold mt-0.5">Under-positioned</p>
        </div>
      </div>
      <div className="py-4 space-y-3 flex-1">
        {CATEGORIES.map((c) => (
          <div key={c.label}>
            <div className="flex items-center justify-between mb-1">
              <span className="font-body text-[10px] text-[#A1A1AA]">{c.label}</span>
              <span className="font-body text-[10px] font-semibold text-[#F8F8F5] tabular-nums">{c.score}</span>
            </div>
            <div className="h-1 bg-[#27272A]">
              <div className="h-full bg-[#DFFF00]" style={{ width: `${c.score}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div className="pt-3 border-t border-[#27272A]">
        <p className="tracking-label text-[8px] uppercase font-body font-bold text-[#DFFF00]">Priority action</p>
        <p className="font-body text-[11px] text-[#A1A1AA] leading-snug mt-1">
          Reframe duties as quantified achievements aligned to target roles.
        </p>
      </div>
    </div>
  );
}

export default function Hero() {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 22;
      const y = (e.clientY / window.innerHeight - 0.5) * 22;
      setTilt({ x, y });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const go = (href) => scrollToId(href);

  return (
    <section data-testid="hero" className="relative w-full overflow-hidden pt-10 md:pt-14 pb-16 md:pb-24">
      {/* Floating scorecard mock-up */}
      <motion.div
        className="absolute right-[5%] top-4 hidden lg:block"
        aria-hidden="true"
      >
        <div style={{ perspective: 1000 }}>
          <motion.div
            animate={{ rotateY: tilt.x, rotateX: -tilt.y }}
            transition={{ type: "spring", stiffness: 60, damping: 18 }}
            className="relative w-[320px] h-[450px]"
            style={{ transformStyle: "preserve-3d" }}
          >
            <div className="absolute inset-0 bg-[#141414] border border-[#27272A] rotate-6 translate-x-4 translate-y-4" />
            <div className="absolute inset-0 bg-[#1A1A1A] border border-[#27272A] rotate-3 translate-x-2 translate-y-2" />
            <ScorecardMock />
          </motion.div>
        </div>
      </motion.div>

      <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00] mb-8"
        >
          Career Strategy · CV · LinkedIn
        </motion.p>

        <h1 className="font-display font-bold tracking-tighter leading-[0.92] text-[11vw] md:text-[5.5vw] text-[#F8F8F5] lg:max-w-[62vw]">
          <MaskLine delay={0.15}>Your skills and experience.</MaskLine>
          <MaskLine delay={0.3}>
            A clear plan for what <span className="font-serif-accent font-light text-[#DFFF00]">comes next.</span>
          </MaskLine>
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8 lg:pr-[380px]">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            className="font-body text-base md:text-lg text-[#A1A1AA] leading-relaxed max-w-md"
          >
            Whether you are looking for a job, changing direction or exploring a small business,
            Barrett Career Brand helps you explain what you offer and plan your next step.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
          >
            <Magnetic
              data-testid="hero-analyze-cta"
              onClick={() => go("#cv-review")}
              className="group rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-7 py-4 flex items-center gap-2 hover:bg-[#C8E600] transition-colors duration-300"
            >
              Get your free CV Career Brand Score
              <ArrowDownRight size={18} className="group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform" />
            </Magnetic>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
